import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Shield, Award, Calendar, Clock, Users, FileText, AlertTriangle,
  CheckCircle, XCircle, Star, TrendingUp, ClipboardList, Bell,
  RefreshCw, Plus, Eye, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkforceManagement = () => {
  const [activeTab, setActiveTab] = useState('credentials');
  const [credentials, setCredentials] = useState([]);
  const [credentialStats, setCredentialStats] = useState({});
  const [expiringCredentials, setExpiringCredentials] = useState([]);
  const [competencyGaps, setCompetencyGaps] = useState({});
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [ptoRequests, setPtoRequests] = useState([]);
  const [ptoStats, setPtoStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPtoDialog, setShowPtoDialog] = useState(false);
  const [ptoForm, setPtoForm] = useState({
    type: 'vacation',
    start_date: '',
    end_date: '',
    hours: '',
    reason: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchCredentials(),
        fetchExpiringCredentials(),
        fetchCompetencyGaps(),
        fetchPerformanceReviews(),
        fetchPtoRequests()
      ]);
    } catch (error) {
      console.error('Error fetching workforce data:', error);
      toast.error('Failed to load workforce data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredentials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/credentials`);
      const data = await response.json();
      setCredentials(data.credentials || []);
      setCredentialStats(data.statistics || {});
    } catch (error) {
      console.error('Error fetching credentials:', error);
    }
  };

  const fetchExpiringCredentials = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/credentials/expiring?days=90`);
      const data = await response.json();
      setExpiringCredentials(data.expiring_credentials || []);
    } catch (error) {
      console.error('Error fetching expiring credentials:', error);
    }
  };

  const fetchCompetencyGaps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/competencies/gaps`);
      const data = await response.json();
      setCompetencyGaps(data);
    } catch (error) {
      console.error('Error fetching competency gaps:', error);
    }
  };

  const fetchPerformanceReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/performance/reviews`);
      const data = await response.json();
      setPerformanceReviews(data.reviews || []);
      setReviewStats(data.statistics || {});
    } catch (error) {
      console.error('Error fetching performance reviews:', error);
    }
  };

  const fetchPtoRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/pto/requests`);
      const data = await response.json();
      setPtoRequests(data.requests || []);
      setPtoStats(data.statistics || {});
    } catch (error) {
      console.error('Error fetching PTO requests:', error);
    }
  };

  const handlePtoSubmit = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase50/pto/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ptoForm)
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        setShowPtoDialog(false);
        fetchPtoRequests();
        setPtoForm({ type: 'vacation', start_date: '', end_date: '', hours: '', reason: '' });
      }
    } catch (error) {
      console.error('Error submitting PTO:', error);
      toast.error('Failed to submit PTO request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': case 'competent': case 'approved': case 'completed': return 'bg-green-100 text-green-800';
      case 'expiring_soon': case 'developing': case 'pending': case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'expired': case 'not_competent': case 'denied': case 'overdue': return 'bg-red-100 text-red-800';
      case 'needs_renewal': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency) {
      case 'critical': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'warning': return <Bell className="h-4 w-4 text-yellow-500" />;
      default: return <Clock className="h-4 w-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading workforce data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Users className="h-10 w-10 text-teal-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800" data-testid="workforce-title">Workforce Management</h1>
                <p className="text-slate-500">Credentials, competencies, performance, and time off</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAllData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 gap-2 bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="credentials" data-testid="tab-credentials">
              <Shield className="h-4 w-4 mr-2" />
              Credentials
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="tab-competencies">
              <Award className="h-4 w-4 mr-2" />
              Competencies
            </TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">
              <Star className="h-4 w-4 mr-2" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="pto" data-testid="tab-pto">
              <Calendar className="h-4 w-4 mr-2" />
              Time Off
            </TabsTrigger>
          </TabsList>

          {/* Credentials Tab */}
          <TabsContent value="credentials" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">Total Credentials</div>
                  <div className="text-3xl font-bold text-slate-800">{credentialStats.total || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-green-700">Active</div>
                  <div className="text-3xl font-bold text-green-600">{credentialStats.active || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-amber-700">Expiring Soon</div>
                  <div className="text-3xl font-bold text-amber-600">{credentialStats.expiring_soon || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-red-700">Expired</div>
                  <div className="text-3xl font-bold text-red-600">{credentialStats.expired || 0}</div>
                </CardContent>
              </Card>
            </div>

            {/* Expiring Soon Alert */}
            {expiringCredentials.length > 0 && (
              <Card className="border-l-4 border-l-amber-500">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Credentials Requiring Attention ({expiringCredentials.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {expiringCredentials.map(cred => (
                      <div key={cred.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {getUrgencyIcon(cred.urgency)}
                          <div>
                            <div className="font-medium">{cred.staff_name}</div>
                            <div className="text-sm text-slate-500">{cred.credential_name}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(cred.status)}>{cred.status.replace('_', ' ')}</Badge>
                          <div className="text-sm text-slate-500 mt-1">
                            {cred.days_until_expiration < 0 
                              ? `Expired ${Math.abs(cred.days_until_expiration)} days ago`
                              : `Expires in ${cred.days_until_expiration} days`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Credentials */}
            <Card>
              <CardHeader>
                <CardTitle>All Staff Credentials</CardTitle>
                <CardDescription>License and certification tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {credentials.map(cred => (
                    <div key={cred.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${cred.verified ? 'bg-green-50' : 'bg-slate-50'}`}>
                          {cred.verified ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Clock className="h-5 w-5 text-slate-400" />}
                        </div>
                        <div>
                          <div className="font-medium">{cred.staff_name}</div>
                          <div className="text-sm text-slate-500">{cred.credential_name}</div>
                          <div className="text-xs text-slate-400">#{cred.license_number} • {cred.issuing_state}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(cred.status)}>{cred.status.replace('_', ' ')}</Badge>
                        <div className="text-xs text-slate-500 mt-1">Exp: {cred.expiration_date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Competencies Tab */}
          <TabsContent value="competencies" className="space-y-6">
            {/* Summary Card */}
            <Card className="bg-gradient-to-r from-teal-50 to-white border-teal-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-teal-800">Organization Competency Rate</h3>
                    <p className="text-teal-600 mt-1">
                      {competencyGaps.total_staff_with_gaps || 0} of {competencyGaps.total_staff || 0} staff have skill gaps
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-teal-600">
                      {competencyGaps.organization_competency_rate || 0}%
                    </div>
                    <Progress 
                      value={competencyGaps.organization_competency_rate || 0} 
                      className="w-32 mt-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Competency Gaps */}
            {competencyGaps.gaps?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                    Identified Skill Gaps
                  </CardTitle>
                  <CardDescription>Staff requiring additional training or assessment</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {competencyGaps.gaps?.map((gap, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3">
                          <Award className="h-5 w-5 text-amber-500" />
                          <div>
                            <div className="font-medium">{gap.staff_name}</div>
                            <div className="text-sm text-amber-700">Gap: {gap.competency_gap.replace('_', ' ').toUpperCase()}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-slate-500">Overall Rate</div>
                          <div className="font-medium">{gap.overall_rate}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">Total Reviews</div>
                  <div className="text-3xl font-bold text-slate-800">{reviewStats.total || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-green-700">Completed</div>
                  <div className="text-3xl font-bold text-green-600">{reviewStats.completed || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-blue-700">In Progress</div>
                  <div className="text-3xl font-bold text-blue-600">{reviewStats.in_progress || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-red-700">Overdue</div>
                  <div className="text-3xl font-bold text-red-600">{reviewStats.overdue || 0}</div>
                </CardContent>
              </Card>
            </div>

            {/* Reviews List */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Reviews</CardTitle>
                <CardDescription>Annual and probationary reviews</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {performanceReviews.map(review => (
                    <div key={review.id} className="p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium">{review.staff_name}</h4>
                          <p className="text-sm text-slate-500">
                            Review Period: {review.review_period?.start} - {review.review_period?.end}
                          </p>
                          {review.reviewer_name && (
                            <p className="text-xs text-slate-400">Reviewer: {review.reviewer_name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(review.status)}>{review.status.replace('_', ' ')}</Badge>
                          {review.overall_rating && (
                            <div className="mt-2 flex items-center gap-1 justify-end">
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                              <span className="font-medium">{review.overall_rating}/5</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {review.strengths && (
                        <div className="mt-3 pt-3 border-t">
                          <div className="text-sm">
                            <span className="text-green-600 font-medium">Strengths:</span> {review.strengths.join(', ')}
                          </div>
                          {review.areas_for_improvement && (
                            <div className="text-sm mt-1">
                              <span className="text-amber-600 font-medium">Areas for Growth:</span> {review.areas_for_improvement.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PTO Tab */}
          <TabsContent value="pto" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">Total Requests</div>
                  <div className="text-3xl font-bold text-slate-800">{ptoStats.total || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-amber-700">Pending</div>
                  <div className="text-3xl font-bold text-amber-600">{ptoStats.pending || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-green-700">Approved</div>
                  <div className="text-3xl font-bold text-green-600">{ptoStats.approved || 0}</div>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6">
                  <div className="text-sm text-red-700">Denied</div>
                  <div className="text-3xl font-bold text-red-600">{ptoStats.denied || 0}</div>
                </CardContent>
              </Card>
            </div>

            {/* Request Button */}
            <div className="flex justify-end">
              <Button onClick={() => setShowPtoDialog(true)} data-testid="request-pto-btn">
                <Plus className="h-4 w-4 mr-2" />
                Request Time Off
              </Button>
            </div>

            {/* PTO Requests */}
            <Card>
              <CardHeader>
                <CardTitle>Time Off Requests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ptoRequests.map(request => (
                    <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          request.type === 'vacation' ? 'bg-green-500' :
                          request.type === 'sick' ? 'bg-amber-500' :
                          request.type === 'personal' ? 'bg-indigo-500' : 'bg-slate-500'
                        }`}></div>
                        <div>
                          <div className="font-medium">{request.staff_name}</div>
                          <div className="text-sm text-slate-500 capitalize">{request.type} • {request.hours} hours</div>
                          <div className="text-xs text-slate-400">{request.start_date} - {request.end_date}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(request.status)}>{request.status}</Badge>
                        {request.reason && (
                          <div className="text-xs text-slate-500 mt-1 max-w-[200px] truncate">{request.reason}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* PTO Request Dialog */}
      <Dialog open={showPtoDialog} onOpenChange={setShowPtoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
            <DialogDescription>Submit your PTO request for approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Leave Type</Label>
              <Select value={ptoForm.type} onValueChange={(v) => setPtoForm({...ptoForm, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="personal">Personal Day</SelectItem>
                  <SelectItem value="bereavement">Bereavement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={ptoForm.start_date}
                  onChange={(e) => setPtoForm({...ptoForm, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={ptoForm.end_date}
                  onChange={(e) => setPtoForm({...ptoForm, end_date: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Total Hours</Label>
              <Input 
                type="number" 
                value={ptoForm.hours}
                onChange={(e) => setPtoForm({...ptoForm, hours: e.target.value})}
                placeholder="e.g., 8 for one day"
              />
            </div>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea 
                value={ptoForm.reason}
                onChange={(e) => setPtoForm({...ptoForm, reason: e.target.value})}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPtoDialog(false)}>Cancel</Button>
            <Button onClick={handlePtoSubmit} data-testid="submit-pto-btn">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkforceManagement;
